import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CadaraSettingsService } from './cadara-settings.service';
import { CadaraCompanyMatch, CadaraFacilityMapPage } from './cadara-settings.models';

type FacilityRow = CadaraFacilityMapPage['facilities'][number];



// Cadara settings. Today: the facility mapping the Triple Check "Compare with
// Cadara" mode reads. Facilities match by name; an admin corrects a wrong match.
@Component({
  selector: 'app-cadara-settings',
  templateUrl: './cadara-settings.component.html',
  styleUrls: ['./cadara-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CadaraSettingsComponent implements OnInit, OnDestroy {
  loading = false;
  error = '';
  facilityPage: CadaraFacilityMapPage | null = null;
  // The resident-overlap check, per facility, for the company it was run on.
  matches = new Map<number, { companyId: number; match: CadaraCompanyMatch | null; error: string }>();
  checkingAll = false;

  private destroy$ = new Subject<void>();
  private destroyed = false;

  constructor(
    private service: CadaraSettingsService,
    private toastr: NbToastrService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadFacilities();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  get mappedFacilities(): FacilityRow[] {
    return (this.facilityPage?.facilities ?? []).filter((f) => f.companyId !== null);
  }

  private loadFacilities(): void {
    this.loading = true;
    this.service
      .facilityMap()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => {
          this.facilityPage = page;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.error = err?.error?.error || 'Failed to load the facility map.';
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  companyName(companyId: number | null): string {
    return this.facilityPage?.companies.find((c) => c.companyId === companyId)?.name ?? (companyId ? `#${companyId}` : '');
  }

  // Saved as an override of the name match; 'auto' drops the override again.
  setCompany(facility: FacilityRow, companyId: number | null | 'auto'): void {
    this.service
      .saveFacilityMap(facility.facilityId, companyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.matches.delete(facility.facilityId);
          this.loadFacilities();
          this.toastr.success(companyId === 'auto' ? 'Back to the name match' : 'Mapping saved');
        },
        error: (err) => {
          this.toastr.danger(err?.error?.error || 'Failed to save the mapping.');
          this.cdr.markForCheck();
        },
      });
  }

  checkMatch(facility: FacilityRow, companyId: number): void {
    this.runCheck(facility, companyId).catch(() => undefined);
  }

  // One facility at a time: each check pulls a company's residents from Cadara,
  // which rate-limits bursts.
  async checkAll(): Promise<void> {
    if (this.checkingAll) return;
    this.checkingAll = true;
    for (const facility of this.facilityPage?.facilities ?? []) {
      if (facility.companyId === null || this.destroyed) continue;
      await this.runCheck(facility, facility.companyId).catch(() => undefined);
    }
    this.checkingAll = false;
    this.cdr.markForCheck();
  }

  private runCheck(facility: FacilityRow, companyId: number): Promise<void> {
    this.matches.set(facility.facilityId, { companyId, match: null, error: '' });
    this.cdr.markForCheck();
    return new Promise((resolve, reject) => {
      this.service
        .companyMatch(companyId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (match) => {
            this.matches.set(facility.facilityId, { companyId, match, error: '' });
            this.cdr.markForCheck();
            resolve();
          },
          error: (err) => {
            this.matches.set(facility.facilityId, { companyId, match: null, error: err?.error?.error || 'Check failed.' });
            this.cdr.markForCheck();
            reject(err);
          },
        });
    });
  }

  // "59 of 60 residents are here", plus where the rest landed when it is elsewhere.
  matchText(facility: FacilityRow): string {
    const entry = this.matches.get(facility.facilityId);
    if (!entry) return '';
    if (entry.error) return entry.error;
    if (!entry.match) return 'Checking…';
    const { residentsWithId, facilities } = entry.match;
    if (!residentsWithId) return 'No Cadara resident carries a PCC id.';
    const here = facilities.find((f) => f.facilityId === facility.facilityId)?.residents ?? 0;
    const top = facilities[0];
    const elsewhere = top && top.facilityId !== facility.facilityId ? ` Most are in ${top.facilityName} (${top.residents}).` : '';
    return `${here} of ${residentsWithId} Cadara residents are in this facility.${elsewhere}`;
  }

  trackByFacility = (_: number, f: FacilityRow) => f.facilityId;
}
